import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { LegalDisclaimer } from "../components/LegalDisclaimer";
import {
  ArrowLeft, ArrowRight, CheckCircle, FloppyDisk, FileText,
  ShieldWarning, Lightbulb, X, ArrowSquareOut,
} from "@phosphor-icons/react";

const Formation1023EZ = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [filing, setFiling] = useState(null);
  const [eligQuestions, setEligQuestions] = useState([]);
  const [formSteps, setFormSteps] = useState([]);
  const [eligAnswers, setEligAnswers] = useState({});
  const [currentStepIdx, setCurrentStepIdx] = useState(0);
  const [stepValue, setStepValue] = useState("");
  const [reviewMode, setReviewMode] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/formation/1023-ez`);
      setFiling(res.data.filing);
      setEligQuestions(res.data.eligibility_questions);
      setFormSteps(res.data.form_steps);
      setEligAnswers(res.data.filing.eligibility_answers || {});
      const cs = res.data.filing.current_step || 0;
      setCurrentStepIdx(Math.min(cs, res.data.form_steps.length - 1));
      if (res.data.filing.status === "ready_to_review") setReviewMode(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load filing");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // When current step changes, prime the local input with the saved answer
  useEffect(() => {
    if (filing && formSteps.length > 0 && currentStepIdx < formSteps.length) {
      const stepId = formSteps[currentStepIdx]?.id;
      const saved = (filing.answers || {})[stepId];
      setStepValue(saved ?? "");
    }
  }, [currentStepIdx, filing, formSteps]);

  const submitEligibility = async () => {
    // Validate all answered
    const missing = eligQuestions.filter((q) => !eligAnswers[q.id]);
    if (missing.length > 0) {
      toast.error(`Please answer all ${eligQuestions.length} eligibility questions`);
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/formation/1023-ez/eligibility`, { answers: eligAnswers });
      if (res.data.passed) {
        toast.success("Eligibility confirmed — let's walk through the form.");
        await load();
      } else {
        toast.error("Not eligible for 1023-EZ — we'll guide you to the full Form 1023 instead.");
        await load();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to submit");
    } finally {
      setSaving(false);
    }
  };

  const saveStep = async (advance = true) => {
    const step = formSteps[currentStepIdx];
    if (!step) return;
    setSaving(true);
    try {
      const res = await axios.patch(`${API}/formation/1023-ez/step/${step.id}`, {
        answer: stepValue,
        advance,
      });
      // Update local filing state
      setFiling((f) => ({ ...f, answers: res.data.answers, current_step: res.data.current_step, status: res.data.status }));
      if (advance) {
        if (currentStepIdx + 1 >= formSteps.length) {
          setReviewMode(true);
          toast.success("All steps complete — review your answers below.");
        } else {
          setCurrentStepIdx(currentStepIdx + 1);
        }
      } else {
        toast.success("Saved");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const markSubmitted = async () => {
    if (!window.confirm("Confirm: have you completed the 1023-EZ submission at pay.gov?")) return;
    try {
      await axios.post(`${API}/formation/1023-ez/mark-submitted`);
      toast.success("Filed! Determination letter expected in ~90 days.");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to mark submitted");
    }
  };

  // ── Loading ──
  if (loading) {
    return (
      <div className="min-h-screen bg-black text-zinc-100 flex items-center justify-center" data-testid="loading-state">
        <p className="text-sm text-zinc-500">Loading your filing...</p>
      </div>
    );
  }

  if (!filing) return null;

  // ── Ineligibility outcome ──
  if (filing.status === "eligibility_failed") {
    return (
      <Shell title="Form 1023-EZ Walkthrough">
        <div className="max-w-2xl mx-auto py-10" data-testid="ineligibility-state">
          <Card className="bg-amber-500/5 border-amber-500/30">
            <CardContent className="p-8">
              <ShieldWarning weight="fill" className="w-10 h-10 text-amber-400 mb-4" />
              <h2 className="text-2xl font-bold mb-3">Not eligible for the 1023-EZ — but you have a path.</h2>
              <p className="text-sm text-zinc-300 mb-3 leading-relaxed">
                Based on your answers, the IRS requires you to file the full <strong>Form 1023</strong> (not the streamlined EZ version). This is normal for organizations that:
              </p>
              <p className="text-xs text-zinc-400 italic mb-5">
                &ldquo;{filing.eligibility_failed_reason}&rdquo;
              </p>
              <p className="text-sm text-zinc-300 mb-5 leading-relaxed">
                The full Form 1023 is longer (~26 pages of supporting narrative) and more expensive ($600 IRS fee vs. $275). But it&apos;s still doable, and many founders successfully file it on their own. We&apos;re building a Form 1023 walkthrough next — in the meantime, schedule a call with Greg to talk through your path.
              </p>
              <div className="flex gap-3">
                <Button onClick={() => navigate("/feedback")} className="bg-emerald-500 hover:bg-emerald-400 text-black font-semibold" data-testid="contact-greg-btn">
                  Schedule a call
                </Button>
                <Button onClick={() => navigate("/formation")} variant="outline" data-testid="back-formation-btn">
                  Back to Formation Toolkit
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </Shell>
    );
  }

  // ── Eligibility quiz (status = eligibility_pending) ──
  if (filing.status === "eligibility_pending") {
    return (
      <Shell title="Form 1023-EZ Walkthrough — Eligibility">
        <div className="max-w-2xl mx-auto py-8" data-testid="eligibility-quiz">
          <div className="mb-6">
            <p className="text-xs uppercase tracking-widest text-emerald-400 font-bold mb-2">Step 0 — Eligibility</p>
            <h1 className="text-3xl font-black tracking-tight mb-3">Can you use the 1023-EZ?</h1>
            <p className="text-sm text-zinc-400 leading-relaxed max-w-xl">
              The IRS requires a quick eligibility check before you file. Most pre-501(c)(3) founders qualify — but a few situations require the full Form 1023 instead. Answer each question honestly. Takes about 3 minutes.
            </p>
          </div>

          <div className="space-y-4">
            {eligQuestions.map((q, idx) => (
              <Card key={q.id} className="bg-zinc-950/50 border-zinc-800" data-testid={`elig-q-${q.id}`}>
                <CardContent className="p-5">
                  <p className="text-sm font-semibold mb-3">
                    <span className="text-zinc-500 mr-2">{idx + 1}.</span>{q.q}
                  </p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setEligAnswers((a) => ({ ...a, [q.id]: "yes" }))}
                      className={`px-5 py-2 rounded-sm text-sm font-bold uppercase tracking-wider transition-colors ${eligAnswers[q.id] === "yes" ? "bg-emerald-500 text-black" : "bg-zinc-900 text-zinc-400 hover:bg-zinc-800"}`}
                      data-testid={`elig-${q.id}-yes`}
                    >
                      Yes
                    </button>
                    <button
                      type="button"
                      onClick={() => setEligAnswers((a) => ({ ...a, [q.id]: "no" }))}
                      className={`px-5 py-2 rounded-sm text-sm font-bold uppercase tracking-wider transition-colors ${eligAnswers[q.id] === "no" ? "bg-emerald-500 text-black" : "bg-zinc-900 text-zinc-400 hover:bg-zinc-800"}`}
                      data-testid={`elig-${q.id}-no`}
                    >
                      No
                    </button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="mt-8 flex items-center justify-between">
            <p className="text-xs text-zinc-500">
              {Object.keys(eligAnswers).length} of {eligQuestions.length} answered
            </p>
            <Button
              onClick={submitEligibility}
              disabled={saving || Object.keys(eligAnswers).length < eligQuestions.length}
              className="bg-emerald-500 hover:bg-emerald-400 text-black font-bold"
              data-testid="submit-eligibility-btn"
            >
              {saving ? "Checking..." : "Check my eligibility"}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </Shell>
    );
  }

  // ── Review screen ──
  if (reviewMode || filing.status === "ready_to_review" || filing.status === "self_submitted") {
    return (
      <Shell title="Form 1023-EZ — Review Your Answers">
        <div className="max-w-3xl mx-auto py-8" data-testid="review-screen">
          <div className="mb-6">
            <p className="text-xs uppercase tracking-widest text-emerald-400 font-bold mb-2">Final step — Review</p>
            <h1 className="text-3xl font-black tracking-tight mb-3">Review your answers before filing.</h1>
            <p className="text-sm text-zinc-400 leading-relaxed max-w-xl">
              These are the exact answers you&apos;ll enter on the IRS 1023-EZ form at pay.gov. Click any line to go back and edit. Once you&apos;re confident, head to pay.gov to submit, then come back and click &ldquo;Mark Submitted.&rdquo;
            </p>
          </div>

          <div className="space-y-3">
            {formSteps.map((step, idx) => {
              const ans = (filing.answers || {})[step.id];
              const displayAns = ans !== undefined && ans !== "" ? String(ans) : <span className="text-rose-400 italic">Not answered</span>;
              return (
                <button
                  key={step.id}
                  type="button"
                  onClick={() => { setReviewMode(false); setCurrentStepIdx(idx); }}
                  className="w-full text-left bg-zinc-950/50 border border-zinc-800 hover:border-emerald-500/50 transition-colors rounded-sm p-4 group"
                  data-testid={`review-row-${step.id}`}
                >
                  <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">{step.irs_field}</p>
                  <p className="text-sm font-semibold text-zinc-200 mb-1">{step.title}</p>
                  <p className="text-sm text-emerald-300 group-hover:text-emerald-200 truncate">{displayAns}</p>
                </button>
              );
            })}
          </div>

          <div className="mt-8 p-5 rounded-sm bg-emerald-500/5 border border-emerald-500/30">
            <p className="text-sm font-semibold text-emerald-300 mb-3">Ready to file?</p>
            <ol className="text-xs text-zinc-300 space-y-1.5 list-decimal list-inside mb-4">
              <li>Go to <a href="https://www.pay.gov/public/form/start/62759871" target="_blank" rel="noreferrer" className="text-emerald-400 hover:underline">pay.gov &mdash; Form 1023-EZ <ArrowSquareOut className="inline w-3 h-3" /></a></li>
              <li>Enter each answer EXACTLY as shown above</li>
              <li>Pay the $275 IRS user fee</li>
              <li>Save your confirmation receipt</li>
              <li>Come back here and click &ldquo;Mark Submitted&rdquo; so we can track your approval window</li>
            </ol>
            {filing.status !== "self_submitted" ? (
              <Button onClick={markSubmitted} className="bg-emerald-500 hover:bg-emerald-400 text-black font-bold" data-testid="mark-submitted-btn">
                <CheckCircle weight="fill" className="w-4 h-4 mr-2" />
                I&apos;ve submitted to pay.gov — mark filed
              </Button>
            ) : (
              <div className="flex items-center gap-2 text-emerald-300" data-testid="filed-state">
                <CheckCircle weight="fill" className="w-5 h-5" />
                <span className="text-sm font-semibold">Filed on {new Date(filing.submitted_at).toLocaleDateString()}. IRS determination letter typically arrives within 90 days.</span>
              </div>
            )}
          </div>
        </div>
      </Shell>
    );
  }

  // ── Single-step wizard ──
  const step = formSteps[currentStepIdx];
  if (!step) return null;
  const progress = Math.round(((currentStepIdx) / formSteps.length) * 100);

  return (
    <Shell title="Form 1023-EZ Walkthrough">
      <div className="max-w-2xl mx-auto py-6" data-testid="wizard-step">
        {/* Progress bar */}
        <div className="mb-8" data-testid="progress-bar">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs uppercase tracking-widest text-emerald-400 font-bold">
              Step {currentStepIdx + 1} of {formSteps.length}
            </p>
            <p className="text-[10px] uppercase tracking-widest text-zinc-500">{step.irs_field}</p>
          </div>
          <div className="h-1 bg-zinc-900 rounded-full overflow-hidden">
            <div className="h-full bg-emerald-500 transition-all duration-300" style={{ width: `${progress}%` }} />
          </div>
        </div>

        {/* Question */}
        <div className="mb-6">
          <h1 className="text-2xl font-black tracking-tight mb-4 leading-tight" data-testid="step-title">{step.title}</h1>
          {step.help && (
            <p className="text-sm text-zinc-400 leading-relaxed mb-3" data-testid="step-help">{step.help}</p>
          )}
          {step.tip && (
            <div className="flex items-start gap-2 p-3 rounded-sm bg-amber-500/5 border border-amber-500/30">
              <Lightbulb weight="fill" className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-200 leading-relaxed" data-testid="step-tip"><strong>Tip:</strong> {step.tip}</p>
            </div>
          )}
        </div>

        {/* Input */}
        <div className="mb-8" data-testid="step-input">
          {step.kind === "text" && (
            <Input
              value={stepValue}
              onChange={(e) => setStepValue(e.target.value)}
              placeholder="Type your answer..."
              className="bg-zinc-950/80 border-zinc-800 text-base"
              data-testid="input-text"
            />
          )}
          {step.kind === "longtext" && (
            <Textarea
              value={stepValue}
              onChange={(e) => setStepValue(e.target.value)}
              placeholder="Type your answer..."
              rows={6}
              className="bg-zinc-950/80 border-zinc-800 text-base"
              data-testid="input-longtext"
            />
          )}
          {step.kind === "date" && (
            <Input
              type="text"
              value={stepValue}
              onChange={(e) => setStepValue(e.target.value)}
              placeholder="MM/DD/YYYY"
              className="bg-zinc-950/80 border-zinc-800 text-base"
              data-testid="input-date"
            />
          )}
          {step.kind === "yesno" && (
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setStepValue("yes")}
                className={`px-6 py-3 rounded-sm text-sm font-bold uppercase tracking-wider transition-colors ${stepValue === "yes" ? "bg-emerald-500 text-black" : "bg-zinc-900 text-zinc-400 hover:bg-zinc-800"}`}
                data-testid="input-yes"
              >
                Yes
              </button>
              <button
                type="button"
                onClick={() => setStepValue("no")}
                className={`px-6 py-3 rounded-sm text-sm font-bold uppercase tracking-wider transition-colors ${stepValue === "no" ? "bg-emerald-500 text-black" : "bg-zinc-900 text-zinc-400 hover:bg-zinc-800"}`}
                data-testid="input-no"
              >
                No
              </button>
            </div>
          )}
          {step.kind === "select" && (
            <div className="flex flex-col gap-2">
              {step.options.map((opt) => (
                <button
                  key={opt}
                  type="button"
                  onClick={() => setStepValue(opt)}
                  className={`px-4 py-3 rounded-sm text-sm text-left font-semibold transition-colors ${stepValue === opt ? "bg-emerald-500 text-black" : "bg-zinc-900 text-zinc-300 hover:bg-zinc-800"}`}
                  data-testid={`input-option-${opt.slice(0, 20)}`}
                >
                  {opt}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Nav */}
        <div className="flex items-center justify-between" data-testid="wizard-nav">
          <Button
            onClick={() => setCurrentStepIdx(Math.max(0, currentStepIdx - 1))}
            disabled={currentStepIdx === 0 || saving}
            variant="outline"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />Back
          </Button>

          <div className="flex items-center gap-2">
            <Button
              onClick={() => saveStep(false)}
              disabled={saving || stepValue === ""}
              variant="outline"
              className="border-zinc-700"
              data-testid="save-exit-btn"
            >
              <FloppyDisk className="w-4 h-4 mr-2" />Save &amp; exit
            </Button>
            <Button
              onClick={() => saveStep(true)}
              disabled={saving || stepValue === ""}
              className="bg-emerald-500 hover:bg-emerald-400 text-black font-bold"
              data-testid="next-btn"
            >
              {saving ? "Saving..." : (currentStepIdx + 1 === formSteps.length ? "Finish" : "Next")}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>

        {/* Jump to review (if past step 1 and not on final) */}
        {currentStepIdx > 0 && currentStepIdx < formSteps.length - 1 && Object.keys(filing.answers || {}).length === formSteps.length && (
          <button
            type="button"
            onClick={() => setReviewMode(true)}
            className="mt-6 text-xs text-emerald-400 hover:text-emerald-300 underline"
            data-testid="jump-to-review-btn"
          >
            All steps answered — jump to review
          </button>
        )}
      </div>
    </Shell>
  );
};

// ── Shell wrapper ──
const Shell = ({ title, children }) => {
  return (
    <div className="min-h-screen bg-black text-zinc-100">
      <header className="border-b border-zinc-800/60 bg-zinc-950/70 backdrop-blur sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <Link to="/formation" className="inline-flex items-center gap-2 text-zinc-400 hover:text-emerald-400 text-sm" data-testid="back-to-formation-link">
            <ArrowLeft className="w-4 h-4" />
            Formation Toolkit
          </Link>
          <div className="flex items-center gap-2 text-xs text-zinc-500">
            <FileText className="w-4 h-4" />
            {title}
          </div>
        </div>
      </header>
      <main className="px-4 sm:px-6">
        {children}
        <div className="max-w-4xl mx-auto">
          <LegalDisclaimer variant="footer" />
        </div>
      </main>
    </div>
  );
};

export default Formation1023EZ;
